/**
 * Slider Component for Poker Reborn
 *
 * A draggable slider for numeric value selection (e.g., volume controls).
 *
 * @see docs/screens/settings/README.md - Used in Settings screen
 */

import { Container, Graphics, Text, FederatedPointerEvent } from 'pixi.js';

// =============================================================================
// TYPES
// =============================================================================

export interface SliderStyle {
  /** Track background color */
  trackColor: number;

  /** Track fill color (left of thumb) */
  fillColor: number;

  /** Thumb color */
  thumbColor: number;

  /** Thumb hover color */
  thumbHoverColor: number;

  /** Track height */
  trackHeight: number;

  /** Thumb radius */
  thumbRadius: number;

  /** Label color */
  labelColor: number;

  /** Value color */
  valueColor: number;

  /** Font size */
  fontSize: number;

  /** Font family */
  fontFamily: string;
}

export interface SliderOptions {
  /** Slider width */
  width: number;

  /** Minimum value */
  min?: number;

  /** Maximum value */
  max?: number;

  /** Current value */
  value?: number;

  /** Step increment */
  step?: number;

  /** Label text */
  label?: string;

  /** Show value as percentage */
  showPercentage?: boolean;

  /** Custom value formatter (overrides showPercentage) */
  formatValue?: (value: number) => string;

  /** Hide the built-in value text display */
  hideValueText?: boolean;

  /** Custom style overrides */
  style?: Partial<SliderStyle>;
}

// =============================================================================
// DEFAULT STYLE
// =============================================================================

const DEFAULT_STYLE: SliderStyle = {
  trackColor: 0x333344,
  fillColor: 0x4a8a44,
  thumbColor: 0xffffff,
  thumbHoverColor: 0xcccccc,
  trackHeight: 8,
  thumbRadius: 12,
  labelColor: 0xcccccc,
  valueColor: 0xffffff,
  fontSize: 14,
  fontFamily: 'Roboto, Arial, sans-serif',
};

// =============================================================================
// SLIDER COMPONENT
// =============================================================================

export class Slider extends Container {
  private track: Graphics;
  private fill: Graphics;
  private thumb: Graphics;
  private labelText: Text | null = null;
  private valueText: Text | null = null;

  private style: SliderStyle;
  private sliderWidth: number;
  private min: number;
  private max: number;
  private step: number;
  private _value: number;
  private showPercentage: boolean;
  private customFormatValue: ((value: number) => string) | undefined;

  private isDragging = false;
  private isHovering = false;

  /** Callback when value changes */
  public onChange?: (value: number) => void;

  constructor(options: SliderOptions) {
    super();

    this.style = { ...DEFAULT_STYLE, ...options.style };
    this.sliderWidth = options.width;
    this.min = options.min ?? 0;
    this.max = options.max ?? 1;
    this.step = options.step ?? 0.01;
    this._value = options.value ?? this.min;
    this.showPercentage = options.showPercentage ?? true;
    this.customFormatValue = options.formatValue;

    // Create label if provided
    if (options.label) {
      this.labelText = new Text({
        text: options.label,
        style: {
          fill: this.style.labelColor,
          fontSize: this.style.fontSize,
          fontFamily: this.style.fontFamily,
        },
      });
      this.addChild(this.labelText);
    }

    // Create value text (unless hidden)
    if (!options.hideValueText) {
      this.valueText = new Text({
        text: this.formatValue(),
        style: {
          fill: this.style.valueColor,
          fontSize: this.style.fontSize,
          fontFamily: this.style.fontFamily,
          fontWeight: 'bold',
        },
      });
      this.valueText.anchor.set(1, 0);
      this.addChild(this.valueText);
    }

    // Create track
    this.track = new Graphics();
    this.addChild(this.track);

    // Create fill
    this.fill = new Graphics();
    this.addChild(this.fill);

    // Create thumb
    this.thumb = new Graphics();
    this.thumb.eventMode = 'static';
    this.thumb.cursor = 'pointer';
    this.addChild(this.thumb);

    // Setup interactions
    this.setupInteractions();

    // Initial draw
    this.draw();
  }

  /** Get current value */
  get value(): number {
    return this._value;
  }

  /** Set current value */
  set value(newValue: number) {
    const clamped = Math.max(this.min, Math.min(this.max, newValue));
    const stepped = Math.max(this.min, Math.min(this.max, Math.round(clamped / this.step) * this.step));

    if (stepped !== this._value) {
      this._value = stepped;
      this.draw();
      this.onChange?.(this._value);
    }
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupInteractions(): void {
    // Make entire slider area interactive for easier dragging
    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Create a large hit area covering the entire slider
    this.hitArea = {
      contains: (x: number, y: number) => {
        const trackY = this.labelText ? 24 : 0;
        const hitPadding = 10;
        return (
          x >= -hitPadding &&
          x <= this.sliderWidth + hitPadding &&
          y >= trackY - hitPadding &&
          y <= trackY + this.style.thumbRadius * 2 + hitPadding
        );
      },
    };

    // Track and thumb visuals (not interactive themselves)
    this.track.eventMode = 'none';
    this.thumb.eventMode = 'none';

    // Slider area interactions
    this.on('pointerdown', this.onPointerDown, this);
    this.on('pointermove', this.onPointerMove, this);
    this.on('pointerup', this.onPointerUp, this);
    this.on('pointerupoutside', this.onPointerUp, this);
    this.on('pointerover', this.onThumbOver, this);
    this.on('pointerout', this.onThumbOut, this);
  }

  private onPointerDown(event: FederatedPointerEvent): void {
    this.isDragging = true;
    this.updateValueFromPointer(event);
  }

  private onPointerMove(event: FederatedPointerEvent): void {
    if (!this.isDragging) return;
    this.updateValueFromPointer(event);
  }

  private onPointerUp(): void {
    this.isDragging = false;
    this.drawThumb();
  }

  private updateValueFromPointer(event: FederatedPointerEvent): void {
    const local = this.toLocal(event.global);
    const ratio = Math.max(0, Math.min(1, local.x / this.sliderWidth));
    this.value = this.min + ratio * (this.max - this.min);
  }

  private onThumbOver(): void {
    this.isHovering = true;
    this.drawThumb();
  }

  private onThumbOut(): void {
    this.isHovering = false;
    this.drawThumb();
  }

  private draw(): void {
    const trackY = this.labelText ? 24 : 0;
    const trackCenterY = trackY + this.style.thumbRadius;

    // Position label
    if (this.labelText) {
      this.labelText.x = 0;
      this.labelText.y = 0;
    }

    // Position value text
    if (this.valueText) {
      this.valueText.text = this.formatValue();
      this.valueText.x = this.sliderWidth;
      this.valueText.y = 0;
    }

    // Draw track
    this.track.clear();
    this.track.roundRect(
      0,
      trackCenterY - this.style.trackHeight / 2,
      this.sliderWidth,
      this.style.trackHeight,
      this.style.trackHeight / 2
    );
    this.track.fill(this.style.trackColor);

    // Draw fill
    const ratio = (this._value - this.min) / (this.max - this.min);
    const fillWidth = ratio * this.sliderWidth;

    this.fill.clear();
    if (fillWidth > 0) {
      this.fill.roundRect(
        0,
        trackCenterY - this.style.trackHeight / 2,
        fillWidth,
        this.style.trackHeight,
        this.style.trackHeight / 2
      );
      this.fill.fill(this.style.fillColor);
    }

    // Draw thumb
    this.drawThumb();
  }

  private drawThumb(): void {
    const trackY = this.labelText ? 24 : 0;
    const trackCenterY = trackY + this.style.thumbRadius;
    const ratio = (this._value - this.min) / (this.max - this.min);
    const thumbX = ratio * this.sliderWidth;

    const color = this.isHovering || this.isDragging
      ? this.style.thumbHoverColor
      : this.style.thumbColor;

    this.thumb.clear();
    this.thumb.circle(thumbX, trackCenterY, this.style.thumbRadius);
    this.thumb.fill(color);
    this.thumb.circle(thumbX, trackCenterY, this.style.thumbRadius);
    this.thumb.stroke({ color: 0x000000, width: 2, alpha: 0.3 });
  }

  private formatValue(): string {
    if (this.customFormatValue) {
      return this.customFormatValue(this._value);
    }
    if (this.showPercentage) {
      return `${Math.round(this._value * 100)}%`;
    }
    return this._value.toFixed(2);
  }
}
